<template>
  <div
    :class="[
      'subscribe',
      { 'subscribe--dark': isDarkTheme },
      { 'subscribe--alone': isStandalone }
    ]"
  >
    <p v-if="hasTitle" class="subscribe__title">{{ $t('SUBSCRIBE_TITLE') }}</p>
    <i18n tag="p" class="subscribe__desc" path="SUBSCRIBE_DETAIL">
      <a :href="$t('SUBSCRIBE_LINK')">{{ $t('SUBSCRIBE_LINK_TEXT') }}</a>
    </i18n>
    <form
      class="subscribe__form"
      @submit.prevent="handleSubscribe"
    >
      <TextInput
        ref="textInput"
        id="subscribe_email"
        class="subscribe__input"
        v-model="email"
        v-validate="'required|email'"
        data-vv-validate-on="blur"
        :errorMessage="errors.first($t('SUBSCRIBE_LABEL'))"
        :successMessage="$t(successText)"
        :name="$t('SUBSCRIBE_LABEL')"
        :label="$t('SUBSCRIBE_LABEL')"
      />
      <ShButton
        type="submit"
        class="subscribe__btn"
        :color="btnColor"
      >
        {{ $t('SUBSCRIBE_SUBMIT') }}
      </ShButton>
    </form>
    <RecaptchaTerms class="subscribe__terms" />
  </div>
</template>

<script>
import { TextInput, ShButton } from '@sohohouse/design-system'
import { createNamespacedHelpers } from 'vuex'

import CTA from '~/constants/appearance'
import RecaptchaV3 from '~/mixins/recaptcha'
import RecaptchaTerms from '~/components/recaptcha-terms'

import SUBSCRIBE_ERRORS from './error-messages'

const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')
const { mapActions: mapAccountActions, mapGetters: mapAccountGetters } = createNamespacedHelpers('user/account')

export default {
  name: 'Subscribe',

  $_veeValidate: {
    validator: 'new'
  },

  components: {
    TextInput,
    ShButton,
    RecaptchaTerms
  },

  props: {
    isStandalone: {
      type: Boolean,
      default: false
    },

    hasTitle: {
      type: Boolean,
      default: true
    }
  },

  mixins: [
    RecaptchaV3
  ],

  data () {
    return {
      email: '',
      success: false,
      policy: true, // TODO - designs currently don't iinclude a policy cehckbox,
      timer: null
    }
  },

  mounted () {
    this.$validator.reset()
    this.$validator.localize(this.$i18n.locale, {
      messages: {
        required: this.$t('SUBSCRIBE_REQUIRED_EMAIL'),
        email: this.$t('SUBSCRIBE_INVALID_EMAIL')
      }
    })
  },

  destroyed () {
    clearTimeout(this.timer)
  },

  computed: {
    ...mapEnvGetters(['isDarkTheme']),
    ...mapAccountGetters(['failed', 'errorMessages']),

    isInvalid () {
      return Object.values(this.veeValidateFields)
        .some(field => (field.required && field.pristine) || field.invalid)
    },

    btnColor () {
      return this.isDarkTheme ? CTA.secondary : CTA.primary
    },

    successText () {
      return this.success ? 'SUBSCRIBE_SUCCESS' : ''
    }
  },

  watch: {
    email (value) {
      if (value.length > 0 && this.success) {
        this.success = false
      }
    }
  },

  methods: {
    ...mapAccountActions(['subscribe']),

    async handleSubscribe () {
      await this.$validator.validate()

      if (this.isInvalid) {
        return
      }

      const token = await this.getRecaptchaToken()

      const params = {
        email: this.email,
        pageId: location.pathname,
        privacyPolicyConsent: this.policy
      }

      await this.subscribe({ params, token })

      if (this.failed) {
        this.handleErrors()
      } else {
        this.resetForm()
        this.success = true
        // remove focus from input when enter is pressed
        this.removeFocus()
        this.timer = setTimeout(() => {
          this.success = false
        }, 5000)
      }
    },

    handleErrors () {
      // will be undefined if missing network connection
      if (!this.errorMessages || !this.errorMessages.length) {
        this.errors.add({
          field: this.$t('SUBSCRIBE_LABEL'),
          msg: this.$t('SUBSCRIBE_UNEXPECTED_ERROR')
        })
      } else {
        this.errorMessages.forEach(({ code }) => this.errors.add({
          field: this.$t('SUBSCRIBE_LABEL'),
          msg: this.$t(SUBSCRIBE_ERRORS[code])
        }))
      }
    },

    removeFocus () {
      this.$refs.textInput.$refs.input.blur()
    },

    resetForm () {
      this.email = ''
      this.$validator.reset()
    }
  }
}
</script>

<style lang="scss" scoped>
$max-form-width: 465px;

.subscribe {
  &__form {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;

    @include tablet {
      flex-direction: row;
      align-items: flex-end;
      flex-wrap: nowrap;
    }
  }

  &__desc {
    @include font('050');
    margin-bottom: $space-050;
  }

  &__title {
    @include font('050');
    margin-bottom: $space-030;

    @include desktop {
      @include font('070');
    }
  }

  &__input {
    margin-bottom: $space-050;
    width: 100%;

    @include tablet {
      margin-right: $space-030;
      margin-bottom: 0;
    }
  }

  &__btn {
    width: fit-content;
  }

  &__terms {
    margin-top: $space-030;
    @include tablet {
      width: 80%;
    }
  }

  &--dark &__title, &--dark &__desc, &--dark &__input {
    color: $color-white;
  }

  &--alone &__form {
    // input and button shouldn't be large
    @include tablet {
      max-width: $max-form-width;
    }
  }

  &--alone &__title {
    @include font('080');

    @include tablet {
      @include font('090');
    }
  }

  &--alone &__desc {
    @include tablet {
      @include font('070')
    }
  }
}
</style>
